import React, { useEffect, useState } from "react";
import { getAccessToken } from "config";
import MessageIcon from "assets/img/comment_fill.png";
import ImgContainer from "components/common/img";
import { Toast, getFormattedDateString, hasPermission } from "utility/helpers";
import {
  Button,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Send } from "react-feather";
import PropTypes from "prop-types";
import { markComment } from "services/services.projects";
import _ from "lodash";
import { genericPost } from "services/services.general";
import styled from "styled-components";
import SupportingDocumentsModal from "../supportingDocsModal";

const ModalHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ModalHeaderText = styled.p`
  margin-bottom: 0;
  color: ${({ theme }) => theme.colors.headerTextBlue};
`;

const ModalHeaderBorderLeft = styled(Col)`
  height: 3px;
  background: #00316d;
`;

const ModalHeaderBorderRight = styled(Col)`
  height: 3px;
  background: #efefef;
`;

const ModalHeaderBorder = styled(Row)`
  display: flex;
  width: 100%;
  align-items: center;
  margin: 0;
`;

const StyledModal = styled(Modal)`
  --bs-modal-margin: 0 !important;
  margin-right: 0 !important;

  .modal-dialog {
    height: 100%;
  }

  .modal-content {
    height: 100vh;
    border: none;
    border-radius: 0;

    .comment-container {
      .user {
        font-weight: 600;
        font-size: 22px;
        line-height: 119.4%;
        letter-spacing: 0.05em;
        color: #000000;
        margin-bottom: 0;

        &.unread {
          font-weight: 500;
        }
      }

      small {
        font-weight: 300;
        font-size: 14px;
        line-height: 119.4%;

        color: #818181;

        &:first-child {
          margin-right: 1rem;
        }
      }
    }

    .modal-body {
      overflow-y: auto;
    }
  }
`;

const CommentText = styled.p`
  text-align: justify;
`;

const ReplySection = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ReplyAnchor = styled.a`
  font-weight: 400;
  font-size: 16px;
  line-height: 146.4%;
  text-align: right;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.systemBlue};
`;

const NoCommentText = styled.p`
  font-weight: 600;
  font-size: 22px;
  line-height: 119.4%;
  text-align: center;
  letter-spacing: 0.05em;
  color: #888888;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr 50px;
`;

const SendButtonSection = styled.div`
  display: grid;
  justify-content: center;
`;

const StyledInput = styled(Input)`
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  height: 50px;

  &:focus {
    box-shadow: none;
    border-color: ${({ theme }) => theme.colors.systemBlue};
  }
`;

const SupportingDocumentsButton = styled(Button)`
  background: ${({ theme }) => theme.colors.buttonBlue};
  width: 100%;
  margin-top: 0.5rem;
  font-size: 14px;
  border: none;
`;

const StyledModalBody = styled(ModalBody)`
  display: grid;
`;

const SubTextNoComment = styled.p`
  font-weight: 300;
  margin-bottom: 0;
  line-height: 11px;
  text-align: center;
`;

const ModalBodyContainer = styled.div`
  display: grid;
  margin-top: 1.5rem;
  margin-right: 0.5rem;
  margin-left: 0.5rem;
`;

const ModalBodyWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const CommentsModal = ({
  isOpen,
  handleClose,
  comments,
  congID,
  congressmanId,
  fetchComments,
  programID,
  selectedAgency,
  handleFetchTable,
  view,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isSupportingModalOpen, setIsSupportingModalOpen] = useState(false);
  const [commentsArray, setCommentsArray] = useState(comments);
  const [isReply, setIsReply] = useState(false);
  const [parentCommentId, setParentCommentId] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleReplyMessage = () => {
    if (inputValue) {
      setIsLoading(true);

      genericPost({
        url: `/comment/${parentCommentId}/reply`,
        formData: {
          text: inputValue,
        },
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            fetchComments(congID);
            setInputValue("");
            setIsReply(false);
            setParentCommentId("");
            setIsLoading(false);
            handleFetchTable();
          }
        },
      });
    } else {
      Toast({
        type: 4,
        content: "Reply must not be empty",
      });
    }
  };

  const handleSetSupportingDocumentsModalToggle = () => {
    setIsSupportingModalOpen(!isSupportingModalOpen);
  };

  // eslint-disable-next-line consistent-return
  const handleSubmit = _.debounce(() => {
    if (isReply) return handleReplyMessage();

    if (inputValue) {
      setIsLoading(true);

      genericPost({
        url: "/comment",
        ...(view === "others"
          ? {
              headers: {
                "x-auth-token": getAccessToken(),
              },
            }
          : {}),
        formData: {
          text: inputValue,
          recipientId: !view ? congID : null,
          newsId: view === "news" ? congID : null,
          eventId: view === "events" ? congID : null,
        },
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            fetchComments(congID);
            setInputValue("");
            handleFetchTable();
            setIsLoading(false);
          }
        },
      });
    } else {
      Toast({
        type: 4,
        content: "Comment must not be empty",
      });
    }
  }, 2000);

  const handleMarkAsRead = (comment) => {
    if (!comment.isRead) {
      markComment({
        param: comment.id,
        formData: {
          isRead: true,
        },
        callback: ({ isSuccess }) => {
          if (isSuccess) {
            fetchComments(congID);
            handleFetchTable();
          }
        },
      });
    }
  };

  useEffect(() => {
    const coms = [];

    if (comments?.length !== 0) {
      // eslint-disable-next-line array-callback-return
      comments.map((com) => {
        const comment = {
          createdAt: com?.createdAt,
          id: com?.id,
          createdBy: com?.user?.createdBy,
          position:
            com?.user?.position !== "No Available Position yet"
              ? com?.user?.position
              : "",
          text: com?.text,
          isRead: com?.isRead,
        };

        if (!coms.find((c) => c?.id === com?.id)) {
          coms.push(comment);
        }

        if (com?.replies?.length !== 0) {
          // eslint-disable-next-line array-callback-return
          com.replies.map((reply) => {
            const rep = {
              createdAt: reply?.createdAt,
              id: reply?.id,
              createdBy: reply?.user?.createdBy,
              position:
                reply?.user?.position !== "No Available Position yet"
                  ? reply?.user?.position
                  : "",
              text: reply?.text,
              repliedTo: reply?.user?.repliedTo,
              isRead: com?.isRead,
            };

            coms.push(rep);
          });
        }
      });
    }

    setCommentsArray(coms);
  }, [comments]);

  return (
    <StyledModal
      isOpen={isOpen}
      toggle={() => {
        handleClose();
        setCommentsArray([]);
      }}
    >
      <ModalHeader
        toggle={() => {
          handleClose();
          setCommentsArray([]);
        }}
      >
        <ModalHeaderContainer>
          <ImgContainer src={MessageIcon} />
          <ModalHeaderText>{"Comments"}</ModalHeaderText>
        </ModalHeaderContainer>
      </ModalHeader>
      <ModalHeaderBorder>
        <ModalHeaderBorderLeft xs="6" />
        <ModalHeaderBorderRight xs="6" />
      </ModalHeaderBorder>

      <StyledModalBody>
        <ModalBodyContainer>
          {commentsArray?.length === 0 ? (
            <ModalBodyWrapper>
              <NoCommentText>{"No Comments"}</NoCommentText>

              <SubTextNoComment>
                {"Be the first to comment in this program"}
              </SubTextNoComment>
            </ModalBodyWrapper>
          ) : (
            <div className="comment-container">
              {commentsArray?.map((com) => {
                return (
                  <div
                    key={com?.id}
                    onClick={() => handleMarkAsRead(com)}
                    aria-hidden="true"
                    id={com?.id}
                  >
                    <div>
                      <p className={`user ${com?.isRead ? "unread" : ""}`}>
                        {com?.createdBy}
                      </p>
                      <p className={com?.repliedTo ? "mb-0" : ""}>
                        {com?.position !== "" ? (
                          <small>{com?.position}</small>
                        ) : null}
                        <small>
                          {getFormattedDateString(
                            com.createdAt,
                            "EEEE, MMM dd, yyyy"
                          )}
                        </small>
                      </p>
                      {com?.repliedTo && (
                        <p>
                          <small>{`Replied to ${com?.repliedTo}`}</small>
                        </p>
                      )}
                    </div>
                    <div>
                      <CommentText>{com?.text}</CommentText>
                    </div>
                    <ReplySection>
                      <ReplyAnchor
                        href="/"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsReply(true);
                          setParentCommentId(com?.id);
                        }}
                      >
                        {"Reply"}
                      </ReplyAnchor>
                    </ReplySection>
                    <hr />
                  </div>
                );
              })}
            </div>
          )}
        </ModalBodyContainer>
      </StyledModalBody>

      <StyledModalFooter>
        <div>
          <StyledInput
            className={`${isReply ? "is-focused" : ""}`}
            placeholder="Add Comment"
            value={inputValue}
            onChange={(event) => {
              setInputValue(event.target.value);
            }}
          />

          {!view && (
            <SupportingDocumentsButton
              onClick={handleSetSupportingDocumentsModalToggle}
            >
              {"See Supporting Documents"}
            </SupportingDocumentsButton>
          )}
        </div>

        <SendButtonSection>
          {hasPermission("comment-create") && (
            <a
              href="!#"
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="dark"
                  size="sm"
                />
              ) : (
                <Send
                  color="#00316d"
                  size={20}
                />
              )}
            </a>
          )}
        </SendButtonSection>
      </StyledModalFooter>

      <SupportingDocumentsModal
        congID={congID}
        congressmanId={congressmanId}
        isOpen={isSupportingModalOpen}
        programID={programID}
        selectedAgency={selectedAgency}
        setModal={handleSetSupportingDocumentsModalToggle}
      />
    </StyledModal>
  );
};

CommentsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  comments: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.array,
  ]),
  congID: PropTypes.PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  congressmanId: PropTypes.string,
  fetchComments: PropTypes.func.isRequired,
  programID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedAgency: PropTypes.oneOfType([PropTypes.object]),
  handleFetchTable: PropTypes.func,
};

export default CommentsModal;
